import { Box } from "@chakra-ui/react";
import React from "react";
import Balancer from "react-wrap-balancer";
import {
  AlertIcon,
  CornerDownRightIcon,
  GiftIcon,
  GlobeIcon,
  HeartIcon,
  LightningIcon,
  MailIcon,
  MinusCircleIcon,
  MobilePhoneIcon,
  MoneyIcon,
  PercentIcon,
  PersonWithCheckIcon,
  PinIcon,
  RefreshIcon,
  SearchIcon,
  SettingsIcon,
  ShoppingCartIcon,
  SunshineIcon,
  TargetIcon,
  TrendIcon,
} from "ui/icons";
import hero from "../../../../public/assets/cdp-industries/heros/consumer-subscriptions.png";
import { dataBreachStat, personalizedExperiencesStat, type CdpIndustryConfig } from "./shared";

export const consumeSubscriptionsCdpConfig: CdpIndustryConfig = {
  icon: RefreshIcon,
  name: "consumer subscriptions",
  pluralNoun: "subscription companies",
  color: "green",
  seoCopy:
    "Leverage all your consumer subscriptions data, including subscribers, plans, billing records, churn metrics, your product catalog, and trial conversions.",
  heroImage: {
    src: hero,
    alt: "A photograph of a plated dinner with a list of example data tables overlayed. The data tables included are for product catalog, billing records, subscribers, trial conversion, and plans.",
  },
  adoptingCdpsDescription:
    "Consumer subscriptions are all about loyalty. Customers expect and demand continuous value and if you’re not delivering on that value, they’ll move on. The only competitive advantage you have is data. ",
  featuredOutcomes: [
    {
      icon: TrendIcon,
      color: "blue",
      title: "Grow LTV",
    },
    {
      icon: PersonWithCheckIcon,
      color: "violet",
      title: "Improve customer acquisition",
    },
    {
      icon: RefreshIcon,
      color: "yellow",
      title: "Reduce churn",
    },
  ],
  dataStatsTitleMaxW: "4xl",
  dataStats: [
    {
      title: "$3T",
      description: (
        <Balancer>
          estimated market size for the <Box as="br" display={{ base: "block", lg: "none" }} />
          consumer subscription industry
        </Balancer>
      ),
      href: "https://subta.com/2024-state-of-subscription-commerce-comprehensive-insights-and-trends/",
    },
    personalizedExperiencesStat,
    dataBreachStat,
  ],
  personalizationCards: [
    {
      title: "HelloFresh uses machine learning to provide personalized meal recommendations",
      href: "https://engineering.hellofresh.com/enhancing-the-customer-experience-with-machine-learning-personalized-meal-recommendations-using-2277bf862da4",
    },
    {
      title: "Dollar Shave Club is powering personalized product recommendations and website optimization",
      href: "https://aws.amazon.com/solutions/case-studies/dollar-shave-club-case-study/",
    },
    {
      title: "FabFitFun creates highly curated emails for members to drive shopping participation",
      href: "/customers/fabfitfun",
    },
  ],
  useCases: {
    performanceMarketing: [
      // First row
      {
        icon: PersonWithCheckIcon,
        title: "Acquisition campaigns",
        description:
          "Drive new subscriptions by using browsing data to target potential subscribers and lookalike audiences with offers like free trials or discounted plans, increasing customer acquisition.",
      },
      {
        icon: MinusCircleIcon,
        title: "Suppression campaigns",
        description:
          "Reduce wasted ad spend by suppressing active subscribers from acquisition campaigns using CRM data, reallocating budget toward lapsed users and new prospects.",
      },
      {
        icon: TargetIcon,
        title: "Retargeting campaigns",
        description:
          "Re-engage trial users or signup abandoners by serving dynamic ads highlighting premium features, exclusive benefits, or limited-time offers, driving conversions.",
      },
      // Second row
      {
        icon: MoneyIcon,
        title: "Upsell & cross-sell campaigns",
        description:
          "Promote premium plans or add-ons to existing customers by using engagement and usage data to target subscribers with ads for higher-tier plans, increasing ARPU.",
      },
      {
        icon: SettingsIcon,
        title: "Conversion APIs",
        description:
          "Share online and offline conversion data with ad platforms, enabling algorithms to better identify and target users with a higher likelihood of converting to improve campaign performance.",
      },
      {
        icon: GlobeIcon,
        title: "Media networks",
        description:
          "Monetize customer insights by offering anonymized audience segments to subscription-based brands for targeted advertising, generating incremental revenue.",
      },
    ],
    lifecycleMarketing: [
      // First row
      {
        icon: MoneyIcon,
        title: "Upsell & cross-sell campaigns",
        description:
          "Encourage subscribers to upgrade to premium plans, add family members, or purchase exclusive content bundles.",
      },
      {
        icon: RefreshIcon,
        title: "Reactivation campaigns",
        description: "Win back churned subscribers with waived fees, limited-time discounts, or exclusive perks.",
      },
      {
        icon: SunshineIcon,
        title: "Seasonal campaigns",
        description:
          "Offer holiday or themed subscription plans, such as a summer entertainment bundle or a winter wellness package.",
      },
      // Second row
      {
        icon: PersonWithCheckIcon,
        title: "Engagement campaigns",
        description:
          "Provide personalized content recommendations, expert tips, or featured product highlights to maintain user interest.",
      },
      {
        icon: HeartIcon,
        title: "Loyalty rewards campaigns",
        description: "Celebrate long-term subscribers with milestone discounts, free perks, or VIP member benefits.",
      },
      {
        icon: PinIcon,
        title: "Localized promotions",
        description:
          "Offer region-specific benefits such as free delivery for local goods or exclusive streaming partnerships.",
      },
      // Third row
      {
        icon: MobilePhoneIcon,
        title: "Digital adoption campaigns",
        description:
          "Promote in-app features like auto-renewal, subscription pause options, or personalization tools to improve retention.",
      },
      {
        icon: GiftIcon,
        title: "Usage milestone rewards",
        description:
          "Recognize high engagement users with exclusive benefits such as a free bonus box or early access to new releases.",
      },
      {
        icon: AlertIcon,
        title: "Upcoming renewals",
        description: "Send renewal reminders with special incentives to encourage continued subscription loyalty.",
      },
    ],
    onsitePersonalization: [
      // First row
      {
        icon: LightningIcon,
        title: "Dynamic home page content",
        description:
          "Personalize the homepage based on browsing history and subscription preferences to increase engagement and conversions.",
      },
      {
        icon: MobilePhoneIcon,
        title: "Dynamic app experiences",
        description:
          "Highlight key app features based on user activity, such as reordering frequently purchased items, to improve retention and reduce friction.",
      },
      {
        icon: SearchIcon,
        title: "Dynamic search results",
        description:
          "Tailor search filters and results to highlight trending products, best-sellers, or user preferences to accelerate product discovery and repeat purchases.",
      },
      // Second row
      {
        icon: PersonWithCheckIcon,
        title: "Recommended features",
        description:
          "Suggest complementary add-ons, premium upgrades, or additional services based on subscriber behavior to increase revenue.",
      },
      {
        icon: CornerDownRightIcon,
        title: "Next best action",
        description:
          "Encourage high-value actions like completing a profile, exploring new products, or upgrading plans to enhance engagement and satisfaction.",
      },
      {
        icon: ShoppingCartIcon,
        title: "Proactive cart nudges",
        description:
          "Reduce abandonment by reminding users about incomplete sign-ups or expiring offers, increasing conversions.",
      },
      // Third row
      {
        icon: PercentIcon,
        title: "Savings goal tracking",
        description:
          "Show progress toward loyalty rewards such as free gifts or discounts, motivating additional purchases.",
      },
      {
        icon: HeartIcon,
        title: "Personalized rewards tracking",
        description:
          "Highlight available loyalty rewards and suggest actions to maximize benefits, deepening engagement and retention.",
      },
      {
        icon: MailIcon,
        title: "Retention & upgrade offers",
        description:
          "Recommend plan upgrades, exclusive add-ons, or renewal incentives based on user engagement, purchase frequency, and browsing behavior to drive higher retention and lifetime value.",
      },
    ],
  },
  considerations: {
    dataModeling: {
      traditional: "Uses predefined models that may not fully capture churn signals or subscription tier migrations",
      composable: "Supports tailored models to handle subscription plans, churn predictors, and engagement data",
    },
    audienceManagement: {
      traditional:
        "Supports broad segmentation but struggles with identifying retention patterns or high-value subscribers",
      composable:
        "Enables granular segmentation using data on subscription tiers, usage behaviors, and cancellation trends",
    },
    customerJourneys: {
      traditional: "Provides standard templates for campaigns like renewal emails or general promotions",
      composable:
        "Powers fully adaptable journeys for events like tier upgrades, subscription renewals, and trial-to-paid conversions",
    },
    identityResolution: {
      traditional:
        "Relies on out-of-the-box algorithms that may not unify profiles across subscriptions apps and payment platforms",
      composable:
        "Supports custom algorithms to unify profiles across subscription accounts, usage logs, and billing systems",
    },
  },
  whyHightouchTitleMaxW: "750px",
  audienceOverlap: {
    title: "Long-term subscribers who canceled recently",
    firstCategoryLabel: "Churned 12+ month subscribers",
    secondCategoryLabel: "Annual subscribers who switched to monthly before churn",
  },
};
